import { useState } from "react";
import { Plus, Pencil, Trash2, BadgeCheck } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "./ui/button";
import { Pill } from "./Common";
import { RecordDialog } from "./RecordDialog";

const MODULE_LABEL = {
  PEOPLE: "People", COMPLIANCE: "Compliance", INCIDENTS: "Incidents & Records", RISK: "Risk", FINANCE: "Finance",
  PROJECTS: "Projects", PRACTICE: "Practice Suite", CLIENT_PORTAL: "Client Portal", MARKETING: "CRM & Marketing",
};
const NO_SCREENS = new Set(["PEOPLE", "RISK", "PROJECTS", "CLIENT_PORTAL"]);
const REG_FIELDS = [
  { key: "name", label: "Registration / accreditation (e.g. NDIS Registered Provider)", required: true, full: true },
  { key: "registration_number", label: "Registration number" },
  { key: "status", label: "Status", type: "select", options: ["Active", "Pending", "Expired", "Suspended"] },
  { key: "renewal_date", label: "Renewal date", type: "date" },
  { key: "notes", label: "Notes", type: "textarea" },
];

function renewalTone(d) {
  if (!d) return null;
  const days = Math.round((new Date(`${d}T00:00:00`) - new Date(new Date().toDateString())) / 86400000);
  if (days < 0) return ["red", "Overdue"];
  if (days <= 60) return ["amber", `Renew in ${days} days`];
  return ["green", "Current"];
}

export function RegistrationsCard({ acc, reload }) {
  const { user } = useAuth();
  const [edit, setEdit] = useState(null);
  const regs = acc.registrations || [];
  const save = async (list) => {
    try { await api.put("/account/settings", { registrations: list }); toast.success("Registrations saved"); reload(); }
    catch (e) { toast.error(errMsg(e)); throw e; }
  };
  const canEdit = user.role !== "staff";
  return (
    <div className="rounded-md border bg-card p-5" data-testid="registrations-card">
      <div className="flex items-center justify-between mb-3">
        <div className="overline">Registrations & accreditations</div>
        {canEdit && <Button size="sm" variant="outline" onClick={() => setEdit({ i: -1, v: { status: "Active" } })} data-testid="add-registration-btn"><Plus className="h-3.5 w-3.5 mr-1" />Add</Button>}
      </div>
      <div className="space-y-2">
        {regs.map((r, i) => {
          const tone = renewalTone(r.renewal_date);
          return (
            <div key={i} className="flex items-center gap-3 rounded-md border px-3 py-2.5" data-testid={`registration-${i}`}>
              <BadgeCheck className="h-4 w-4 text-[#C2A462] shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate">{r.name}</div>
                <div className="text-xs text-muted-foreground truncate">{[r.registration_number, r.status, r.renewal_date && `Renews ${fmtDate(r.renewal_date)}`].filter(Boolean).join(" · ")}</div>
              </div>
              {tone && <Pill tone={tone[0]}>{tone[1]}</Pill>}
              {canEdit && <>
                <Button size="icon" variant="ghost" onClick={() => setEdit({ i, v: r })} data-testid={`edit-registration-${i}`}><Pencil className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" onClick={() => save(regs.filter((_, j) => j !== i))} data-testid={`delete-registration-${i}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
              </>}
            </div>
          );
        })}
        {!regs.length && <div className="text-sm text-muted-foreground">No registrations yet.</div>}
      </div>
      {edit && <RecordDialog open onOpenChange={() => setEdit(null)} title={edit.i < 0 ? "Add registration" : "Edit registration"} fields={REG_FIELDS} initial={edit.v}
        onSave={async (f) => { await save(edit.i < 0 ? [...regs, f] : regs.map((x, j) => (j === edit.i ? f : x))); setEdit(null); }} />}
    </div>
  );
}

export function ModulesCard({ acc, reload }) {
  const { user, reloadAccounts } = useAuth();
  const owner = user.role === "agency_owner";
  const on = new Set(acc.enabled_modules || []);
  const toggle = async (m) => {
    const next = on.has(m) ? [...on].filter((x) => x !== m) : [...on, m];
    try { await api.put("/account/modules", { enabled_modules: next }); toast.success("Modules updated"); reload(); reloadAccounts?.(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="rounded-md border bg-card p-5" data-testid="modules-card">
      <div className="overline mb-1">Enabled BOS modules</div>
      <p className="text-xs text-muted-foreground mb-3">{owner ? "Switch modules on or off for this entity. The menu and data access follow these settings." : "Set by the Enterprise Owner as part of this entity's subscription."}</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {(acc.all_modules || []).map((m) => (
          <button key={m} disabled={!owner} onClick={() => toggle(m)} data-testid={`module-toggle-${m}`} data-on={on.has(m)}
            className={`rounded-md border px-3 py-2 text-left text-[13px] transition-colors ${on.has(m) ? "border-[#C2A462]/70 bg-[#C2A462]/10 text-[#EBD6A0]" : "border-white/10 text-muted-foreground"} ${owner ? "hover:border-[#C2A462]" : "cursor-default"}`}>
            {MODULE_LABEL[m]}
            {NO_SCREENS.has(m) && <span className="block text-[10px] text-muted-foreground">coming soon</span>}
          </button>
        ))}
      </div>
    </div>
  );
}
